import { camelCase, range } from 'lodash-es';
import * as React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import BooksSelect from '../../../../components/books/common/inputs/BooksSelect';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksDeletableTextInput from '../../../../components/books/model1/forms/BooksDeletableTextInput';
import BooksFormErrorMessage from '../../../../components/books/model1/forms/BooksFormErrorMessage';
import { TextInputType } from '../../../../components/books/model1/forms/BooksTextInput';
import BooksSecurityCodeMessage from '../../../../components/books/model1/texts/BooksSecurityCodeMessage';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as $ from './helper';

interface Props {
  paymentMethod?: IBooks.PaymentMethod;
  creditCard: IBooks.CreditCard;
  onChange: (creditCard: IBooks.CreditCard) => void;
  error?: IBooks.CreditCardError;
}

export const submittable = (creditCard: IBooks.CreditCard): boolean =>
  !!creditCard.cardNumber &&
  !!creditCard.securityCode &&
  !!creditCard.month &&
  !!creditCard.year;

class InputPaymentCreditCard extends React.Component<
  Props & InjectedIntlProps
> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
  }

  private yearOptions = this.createYearOptions();
  private monthOptions = this.createMonthOptions();

  private handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) {
    const {
      currentTarget: { name, value },
    } = e;
    const changedState = $.changeObjectByNestedString(
      { creditCard: { ...this.props.creditCard } },
      name,
      value,
    );
    this.props.onChange((changedState as Props).creditCard);
  }

  private createYearOptions() {
    const thisYear = new Date().getFullYear();
    return range(thisYear, thisYear + 15).map(year => ({
      label: year.toString(),
      value: year,
    }));
  }

  private createMonthOptions() {
    return range(1, 12 + 1).map(month => ({
      label: month.toString(),
      value: month,
    }));
  }

  public render() {
    const { paymentMethod, creditCard, error, intl } = this.props;

    if (!paymentMethod || paymentMethod.method !== 'credit_card') {
      return null;
    }

    return (
      <div>
        {error && error.field === 'card' && (
          <div>
            {intl.formatMessage({
              id: `books.errorMessage.${camelCase(error.type)}`,
            })}
          </div>
        )}
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.creditCardNumber',
          })}
          creditCardLabel={true}
          paddingTop={true}
          requiredLabel={true}
          errorStyle={error && error.field === 'number'}
        >
          <BooksDeletableTextInput
            type={TextInputType.Text}
            value={creditCard.cardNumber || ''}
            name="creditCard.cardNumber"
            onChange={this.handleChange}
          />
        </BooksLabeledContainer>
        {error && error.field === 'number' && (
          <BooksFormErrorMessage>
            {intl.formatMessage({
              id: `books.errorMessage.${camelCase(error.type)}`,
            })}
          </BooksFormErrorMessage>
        )}
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.securityCode',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'cvc'}
        >
          <BooksDeletableTextInput
            type={TextInputType.Text}
            value={creditCard.securityCode || ''}
            name="creditCard.securityCode"
            onChange={this.handleChange}
          />
        </BooksLabeledContainer>
        {error && error.field === 'cvc' && (
          <BooksFormErrorMessage>
            {intl.formatMessage({
              id: `books.errorMessage.${camelCase(error.type)}`,
            })}
          </BooksFormErrorMessage>
        )}
        <BooksSecurityCodeMessage />
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.expirationYear',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'year'}
        >
          <BooksSelect
            name="creditCard.year"
            value={creditCard.year || ''}
            onChange={this.handleChange}
            selected={creditCard.year !== undefined}
          >
            {this.yearOptions.map((o, i) => (
              <FormattedMessage
                id="books.formsLabel.year"
                values={{ year: o.label }}
                key={i}
              >
                {year => <option value={o.value}>{year}</option>}
              </FormattedMessage>
            ))}
          </BooksSelect>
        </BooksLabeledContainer>
        {error && error.field === 'year' && (
          <BooksFormErrorMessage>
            {intl.formatMessage({
              id: `books.errorMessage.${camelCase(error.type)}`,
            })}
          </BooksFormErrorMessage>
        )}
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.expirationMonth',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'month'}
        >
          <BooksSelect
            name="creditCard.month"
            value={creditCard.month || ''}
            onChange={this.handleChange}
            selected={creditCard.month !== undefined}
          >
            {this.monthOptions.map((o, i) => (
              <FormattedMessage
                id="books.formsLabel.month"
                values={{ month: o.label }}
                key={i}
              >
                {month => <option value={o.value}>{month}</option>}
              </FormattedMessage>
            ))}
          </BooksSelect>
        </BooksLabeledContainer>
        {error && error.field === 'month' && (
          <BooksFormErrorMessage>
            {intl.formatMessage({
              id: `books.errorMessage.${camelCase(error.type)}`,
            })}
          </BooksFormErrorMessage>
        )}
      </div>
    );
  }
}

export default injectIntl(InputPaymentCreditCard);
